import React, { useState } from "react";
import styled from "styled-components";
import { MdRefresh } from "react-icons/md";

import ProgressContainer from "./ProgressContainer";
import { RefreshWithSSE, ScanRefreshCandidates } from "../../../api";
import { RefreshCandidate } from "../../../api/types";

const RefreshIconStyle = {
    cursor: "pointer",
    color: "rgba(255, 255, 255, 0.7)",
};

const Button = styled(MdRefresh)`
    transition: 0.2s;
    &:hover {
        transform: scale(1.1);
    }
`;

const Picker = styled.div`
    position: fixed;
    top: 70px;
    right: 20px;
    width: 320px;
    max-height: 420px;
    overflow-y: auto;
    background-color: var(--card-bg-color);
    color: var(--text-color);
    border: 1px solid var(--gray-color);
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 1000;
`;

const PickerHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: var(--text-color);
    cursor: pointer;
    font-size: 16px;
    opacity: 0.7;

    &:hover {
        opacity: 1;
    }
`;

const CandidateButton = styled.button`
    width: 100%;
    border: 1px solid var(--gray-color);
    border-radius: 8px;
    background: transparent;
    color: var(--text-color);
    padding: 10px;
    margin-bottom: 8px;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
    }
`;

const CandidateDate = styled.div`
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
`;

const CandidateMeta = styled.div`
    font-size: 12px;
    opacity: 0.75;
`;

const EmptyMessage = styled.div`
    font-size: 12px;
    opacity: 0.75;
    line-height: 1.5;
`;

interface LogMessage {
    status: string;
    message: string;
    timestamp: string;
}

interface UploadedSong {
    artist: string;
    title: string;
    filename: string;
}

const RefreshButton = () => {
    const [messages, setMessages] = useState<LogMessage[]>([]);
    const [uploadedSongs, setUploadedSongs] = useState<UploadedSong[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [showProgressBar, setShowProgressBar] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const [candidates, setCandidates] = useState<RefreshCandidate[]>([]);
    const [scanError, setScanError] = useState("");

    const finish = () => {
        setIsRefreshing(false);
        setShowProgressBar(true);
        setTimeout(() => setShowProgress(false), 2000);
    };

    const startRefresh = (date: string) => {
        setShowPicker(false);
        setMessages([]);
        setUploadedSongs([]);
        setIsRefreshing(true);
        setShowProgress(true);
        setShowProgressBar(false);

        RefreshWithSSE(
            `/api/goodrecord/refresh?date=${encodeURIComponent(date)}`,
            (data) => {
                const timestamp = new Date().toLocaleTimeString();
                setMessages(prev => [...prev, {
                    status: data.status,
                    message: data.message || "",
                    timestamp,
                }]);
                if (data.song && typeof data.song === "object") {
                    setUploadedSongs(prev => [...prev, data.song as UploadedSong]);
                }
                if (data.status === "completed" || data.status === "error") {
                    finish();
                }
            },
            () => finish(),
            () => finish(),
        );
    };

    const onClick = async () => {
        if (isScanning || isRefreshing) {
            return;
        }

        setIsScanning(true);
        setScanError("");
        setCandidates([]);
        setShowPicker(true);

        try {
            const result = await ScanRefreshCandidates();
            setCandidates(result.dates || []);
        } catch (error) {
            setScanError(error instanceof Error ? error.message : String(error));
        } finally {
            setIsScanning(false);
        }
    };

    const closeProgress = () => {
        setShowProgress(false);
    };

    return (
        <>
            <Button
                color="currentColor"
                onClick={onClick}
                style={{
                    ...RefreshIconStyle,
                    opacity: isRefreshing || isScanning ? 0.5 : 1,
                }}
                size={20}
            />

            {showPicker && (
                <Picker>
                    <PickerHeader>
                        <strong>업로드할 날짜 선택</strong>
                        <CloseButton onClick={() => setShowPicker(false)}>×</CloseButton>
                    </PickerHeader>

                    {isScanning && <EmptyMessage>메일함을 확인하는 중...</EmptyMessage>}
                    {!isScanning && scanError && <EmptyMessage>스캔 실패: {scanError}</EmptyMessage>}
                    {!isScanning && !scanError && candidates.length === 0 && (
                        <EmptyMessage>최근 메일 중 새로 업로드할 녹음본이 없습니다.</EmptyMessage>
                    )}

                    {!isScanning && candidates.map(candidate => (
                        <CandidateButton
                            key={candidate.date}
                            type="button"
                            onClick={() => startRefresh(candidate.date)}
                        >
                            <CandidateDate>{candidate.date}</CandidateDate>
                            <CandidateMeta>
                                메일 {candidate.mail_count}개 · 녹음본 {candidate.attachment_count}개 · 미업로드 {candidate.pending_count}개
                            </CandidateMeta>
                        </CandidateButton>
                    ))}
                </Picker>
            )}

            {showProgress && (
                <ProgressContainer
                    messages={messages}
                    uploadedSongs={uploadedSongs}
                    isRefreshing={isRefreshing}
                    showProgressBar={showProgressBar}
                    onClose={closeProgress}
                />
            )}
        </>
    );
};

export default RefreshButton;
